import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Keeps the delivery estimate in step with the selected size.
 *
 * The messages are decided in Liquid — see blocks/cu-delivery-estimate.liquid, which
 * renders both the current message and a small map of every variant's answer.
 *
 * Horizon does not re-render the section on a variant change: variant-picker.js morphs
 * the picker alone and hands every other component the freshly fetched page through a
 * ProductSelectEvent promise. That fetch is the whole product page, roughly a megabyte,
 * so waiting for it to change one line of text is a visible pause on a phone.
 *
 * So this element updates twice, cheapest first:
 *
 *   1. Synchronously, from `event.detail.variantId` — which variant-picker.js puts on
 *      the event before it starts fetching — against the precomputed map. This is what
 *      the shopper actually sees, and it costs no network at all.
 *   2. When the fetch lands, by morphing itself out of that HTML, the same way
 *      product-inventory.js and product-price.js do. Normally this writes the identical
 *      string and is invisible; it earns its place when the map has no entry for the
 *      variant, and it keeps the block correct if the Liquid and the map ever diverge.
 */
class CuDeliveryEstimate extends Component {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    // Cached rather than re-queried on disconnect: by the time this element is
    // being removed it may already be detached, and `closest` would return null,
    // leaving the listener behind on the section.
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section = null;
  }

  /**
   * Reads the map the block rendered. Parsed on demand rather than cached because a
   * morph replaces this element's children, script tag included.
   * @returns {{messages: Record<string, string>, variants: Record<string, string>} | null}
   */
  get #data() {
    const script = this.querySelector('script[data-cu-delivery-map]');
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  /**
   * @param {string} text - The message to show. Empty hides the bar rather than
   *   leaving a coloured strip with nothing in it.
   */
  #render(text) {
    const target = this.querySelector('[data-key="message"]');
    if (!(target instanceof HTMLElement)) return;

    target.textContent = text;
    target.toggleAttribute('hidden', !text);
  }

  /**
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    // Product cards inside the recommendations further down the page fire the same
    // event; without this the PDP bar would follow whatever card was last touched.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const { variantId } = event.detail ?? {};
    const data = this.#data;
    const state = variantId && data?.variants[variantId];

    if (state && data) {
      const message = data.messages[state];
      if (typeof message === 'string') this.#render(message);
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const incoming = html.querySelector('cu-delivery-estimate');
        if (!incoming) return;

        morph(this, incoming, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cu-delivery-estimate] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('cu-delivery-estimate')) {
  customElements.define('cu-delivery-estimate', CuDeliveryEstimate);
}
